import { useEffect, useState } from "react";
import { api, API_URL, inr, fmtDate, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { FileText, Check, Repeat } from "lucide-react";

const chip = (s) => ({
  Accepted: "bg-emerald-100 text-emerald-700", Converted: "bg-emerald-100 text-emerald-700",
  Sent: "bg-sky-100 text-sky-700", Rejected: "bg-rose-100 text-rose-700",
}[s] || "bg-slate-100 text-slate-600");

export default function PortalQuotations() {
  const [list, setList] = useState([]);
  const load = () => api.get("/portal/quotations").then((r) => setList(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const accept = async (q) => {
    if (!window.confirm(`Accept quotation ${q.number}?`)) return;
    try { await api.post(`/portal/quotations/${q.id}/accept`); toast.success("Quotation accepted — our team has been notified"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const repeat = async (q) => {
    try { const r = await api.post("/portal/repeat-order", { source_type: "quotation", source_id: q.id }); toast.success(r.data.message); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-4" data-testid="portal-quotations">
      <h1 className="text-2xl font-display font-bold text-slate-900 tracking-tight">Quotations</h1>
      {list.map((q) => (
        <div key={q.id} className="bg-white border border-slate-200 rounded-xl p-5">
          <div className="flex flex-wrap items-center gap-3 justify-between">
            <div>
              <span className="font-mono text-sm font-semibold text-slate-900">{q.number}</span>
              <span className={`ml-2 text-[10px] uppercase tracking-wider rounded px-1.5 py-0.5 ${chip(q.status)}`}>
                {q.status}</span>
            </div>
            <div className="text-xs text-slate-500">Date: {fmtDate(q.date)} · Valid until: {q.valid_until ? fmtDate(q.valid_until) : "-"}</div>
          </div>
          <div className="mt-3 overflow-x-auto">
            <table className="w-full text-[13px]">
              <thead className="text-[10px] uppercase tracking-wider text-slate-400 text-left">
                <tr><th className="py-1 pr-3">Product</th><th className="py-1 pr-3">Colour</th><th className="py-1 pr-3">Size</th><th className="py-1 pr-3 text-right">Qty</th><th className="py-1 pr-3 text-right">Unit Price</th><th className="py-1 text-right">Amount</th></tr>
              </thead>
              <tbody>
                {(q.items || []).map((it, i) => (
                  <tr key={i} className="border-t border-slate-100">
                    <td className="py-1.5 pr-3">{it.description || it.product_name}</td>
                    <td className="py-1.5 pr-3">{it.color || "-"}</td>
                    <td className="py-1.5 pr-3">{it.size || it.size_label || "-"}</td>
                    <td className="py-1.5 pr-3 text-right tabular-nums">{+it.quantity || 0} {it.unit}</td>
                    <td className="py-1.5 pr-3 text-right tabular-nums">{inr(it.unit_price || it.sales_rate || 0)}</td>
                    <td className="py-1.5 text-right tabular-nums">{inr((it.quantity || 0) * (it.unit_price || it.sales_rate || 0))}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex flex-wrap items-center justify-between gap-3 mt-3 pt-3 border-t border-slate-100">
            <div className="text-sm font-bold text-slate-900 tabular-nums">Total: {inr(q.total)}</div>
            <div className="flex flex-wrap gap-2">
              <a href={`${API_URL}/portal/quotations/${q.id}/pdf`} target="_blank" rel="noreferrer"
                 className="inline-flex items-center gap-1 text-xs border border-slate-200 rounded-md px-3 py-1.5 text-slate-600 hover:border-slate-400" data-testid={`pq-pdf-${q.id}`}>
                <FileText size={12} /> Quotation {q.number}</a>
              {q.proforma_number && (
                <a href={`${API_URL}/portal/quotations/${q.id}/proforma`} target="_blank" rel="noreferrer"
                   className="inline-flex items-center gap-1 text-xs border border-indigo-200 bg-indigo-50 rounded-md px-3 py-1.5 text-indigo-700 hover:border-indigo-400" data-testid={`pq-proforma-${q.id}`}>
                  <FileText size={12} /> Proforma {q.proforma_number}</a>
              )}
              {!["Accepted", "Converted"].includes(q.status) && (
                <button onClick={() => accept(q)} className="inline-flex items-center gap-1 text-xs bg-emerald-600 hover:bg-emerald-700 text-white rounded-md px-3 py-1.5" data-testid={`pq-accept-${q.id}`}>
                  <Check size={12} /> Accept</button>
              )}
              <button onClick={() => repeat(q)} className="inline-flex items-center gap-1 text-xs border border-slate-200 rounded-md px-3 py-1.5 text-slate-600 hover:border-slate-400" data-testid={`pq-repeat-${q.id}`}>
                <Repeat size={12} /> Reorder</button>
            </div>
          </div>
        </div>
      ))}
      {list.length === 0 && <p className="text-sm text-slate-400 py-8 text-center bg-white border border-slate-200 rounded-xl">No quotations yet.</p>}
    </div>
  );
}
